import { t } from './i18n';
const API_URL = import.meta.env.VITE_API_URL || 'https://turns-untold-api.vilmire.workers.dev';

class ApiClient {
  constructor() {
    this.token = localStorage.getItem('token');
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1초
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  async sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async request(method, path, body = null, retryCount = 0) {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const options = { method, headers };
    if (body) {
      options.body = JSON.stringify(body);
    }

    try {
      const res = await fetch(`${API_URL}${path}`, options);

      // 401 Unauthorized 처리 (세션 만료 — 로그인/회원가입 요청 제외)
      if (res.status === 401 && !path.startsWith('/api/auth/')) {
        localStorage.removeItem('token');
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          window.location.href = '/login';
        }
        throw new Error(t('errors.AUTH_SESSION_EXPIRED', 'Session expired'));
      }

      const data = await res.json();

      if (!data.success) {
        // 서버 에러(5xx)면 재시도
        if (res.status >= 500 && retryCount < this.maxRetries) {
          console.warn(`Retrying ${path} (${retryCount + 1}/${this.maxRetries})...`);
          await this.sleep(this.retryDelay * (retryCount + 1));
          return this.request(method, path, body, retryCount + 1);
        }
        // 에러 코드가 있으면 로케일 메시지 사용, 없으면 서버 메시지 fallback
        const errorMsg = data.code ? t(`errors.${data.code}`, data.error) : (data.error || t('ui.requestFailed'));
        throw new Error(errorMsg);
      }

      return data.data;
    } catch (err) {
      // 네트워크 에러면 재시도
      if (err.name === 'TypeError' && retryCount < this.maxRetries) {
        console.warn(`Network error, retrying ${path} (${retryCount + 1}/${this.maxRetries})...`);
        await this.sleep(this.retryDelay * (retryCount + 1));
        return this.request(method, path, body, retryCount + 1);
      }
      throw err;
    }
  }

  // Auth
  async register(username, password) {
    return this.request('POST', '/api/auth/register', { username, password });
  }

  async login(username, password) {
    const data = await this.request('POST', '/api/auth/login', { username, password });
    this.setToken(data.token);
    return data;
  }

  async getMe() {
    return this.request('GET', '/api/auth/me');
  }

  logout() {
    this.setToken(null);
  }

  // Character
  async createCharacter(name, classId) {
    return this.request('POST', '/api/character', { name, classId });
  }

  async getCharacter() {
    return this.request('GET', '/api/character');
  }

  async getParty() {
    return this.request('GET', '/api/character/party');
  }

  async getProfile() {
    return this.request('GET', '/api/character/profile');
  }

  async getTitles() {
    return this.request('GET', '/api/character/titles');
  }

  async equipTitle(titleId) {
    return this.request('POST', '/api/character/titles/equip', { titleId });
  }

  async getAchievements() {
    return this.request('GET', '/api/character/achievements');
  }

  async claimAchievementReward(achievementId) {
    return this.request('POST', '/api/character/achievements/claim', { achievementId });
  }

  // Card
  async getCards() {
    return this.request('GET', '/api/card');
  }

  async getCodex() {
    // codex는 optional auth - 토큰 있으면 보유 여부 포함
    const headers = { 'Content-Type': 'application/json' };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    const res = await fetch(`${API_URL}/api/card/codex`, { method: 'GET', headers });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || t('ui.requestFailed'));
    return data.data;
  }

  async getCollection() {
    return this.request('GET', '/api/card/collection');
  }

  async getDeck() {
    return this.request('GET', '/api/card/deck');
  }

  async saveDeck(cardIds) {
    return this.request('POST', '/api/card/deck', { cardIds });
  }

  async upgradeCard(cardId) {
    return this.request('POST', '/api/card/upgrade', { cardId });
  }

  async craftCard(cardId) {
    return this.request('POST', '/api/card/craft', { cardId });
  }

  async recycleCard(cardId, quantity) {
    return this.request('POST', '/api/card/recycle', { cardId, quantity });
  }

  async getMaterials() {
    return this.request('GET', '/api/card/materials');
  }

  async unlockStarterCards() {
    return this.request('POST', '/api/card/unlock-starter');
  }

  // Dungeon
  async getDungeons() {
    return this.request('GET', '/api/dungeon');
  }

  async getCurrentDungeon() {
    return this.request('GET', '/api/dungeon/current');
  }

  async enterDungeon(dungeonId, pvpMode = false) {
    return this.request('POST', '/api/dungeon/enter', { dungeonId, pvpMode });
  }

  async nextFloor() {
    return this.request('POST', '/api/dungeon/next');
  }

  async goDeeper() {
    return this.request('POST', '/api/dungeon/go-deeper');
  }

  async handleEvent() {
    return this.request('POST', '/api/dungeon/event');
  }

  async startDungeonBattle() {
    return this.request('POST', '/api/dungeon/battle');
  }

  async fleeDungeon() {
    return this.request('POST', '/api/dungeon/flee');
  }

  async getEndlessRanking() {
    return this.request('GET', '/api/dungeon/endless/ranking');
  }

  // Battle
  async startBattle(monsterIds, type = 'pve') {
    return this.request('POST', '/api/battle/start', { monsterIds, type });
  }

  // PvE 로컬 전투 초기화 (시드 + 초기 상태만 받음)
  async initBattle(monsterTypes) {
    return this.request('POST', '/api/battle/init', { monsterTypes });
  }

  // PvE 로컬 전투 결과 제출 (검증 + 보상)
  async submitBattleResult(battleId, result) {
    return this.request('POST', `/api/battle/${battleId}/submit`, result);
  }

  async getBattle(battleId) {
    return this.request('GET', `/api/battle/${battleId}`);
  }

  async playCard(battleId, cardId, targetId) {
    return this.request('POST', `/api/battle/${battleId}/play`, { cardId, targetId });
  }

  async endTurn(battleId) {
    return this.request('POST', `/api/battle/${battleId}/end-turn`);
  }

  async claimReward(battleId) {
    return this.request('POST', `/api/battle/${battleId}/claim`);
  }

  // Class (전직)
  async getClassOptions() {
    return this.request('GET', '/api/class/options');
  }

  async advanceClass(targetClassId) {
    return this.request('POST', '/api/class/advance', { targetClassId });
  }

  // Mercenary (용병)
  async getMercenaryShop() {
    return this.request('GET', '/api/mercenary/shop');
  }

  async buyMercenary(mercenaryId) {
    return this.request('POST', '/api/mercenary/buy', { mercenaryId });
  }

  async getMercenaryTickets() {
    return this.request('GET', '/api/mercenary/tickets');
  }

  async activateMercenary(ticketId, customName = null) {
    return this.request('POST', '/api/mercenary/activate', { ticketId, customName });
  }

  async getActiveMercenaries() {
    return this.request('GET', '/api/mercenary/active');
  }

  async getMercenaryParty() {
    return this.request('GET', '/api/mercenary/party');
  }

  async deactivateMercenary(mercenaryId) {
    return this.request('POST', '/api/mercenary/deactivate', { mercenaryId });
  }

  // Equipment (장비)
  async getEquipmentShop() {
    return this.request('GET', '/api/equipment/shop');
  }

  async getEquipmentSets() {
    return this.request('GET', '/api/equipment/sets');
  }

  async buyEquipment(equipmentId) {
    return this.request('POST', '/api/equipment/buy', { equipmentId });
  }

  async getEquipmentInventory() {
    return this.request('GET', '/api/equipment/inventory');
  }

  async equipItem(inventoryId) {
    return this.request('POST', '/api/equipment/equip', { inventoryId });
  }

  async unequipItem(slot) {
    return this.request('POST', '/api/equipment/unequip', { slot });
  }

  async getEquipped() {
    return this.request('GET', '/api/equipment/equipped');
  }

  async enhanceItem(inventoryId) {
    return this.request('POST', '/api/equipment/enhance', { inventoryId });
  }

  // PvP
  async getPvpRecord() {
    return this.request('GET', '/api/pvp/record');
  }

  async getPvpRanking() {
    return this.request('GET', '/api/pvp/ranking');
  }

  async joinPvpQueue() {
    return this.request('POST', '/api/pvp/queue');
  }

  async leavePvpQueue() {
    return this.request('DELETE', '/api/pvp/queue');
  }

  async getPvpQueueStatus() {
    return this.request('GET', '/api/pvp/queue/status');
  }

  async reportPvpResult(opponentId, won) {
    return this.request('POST', '/api/pvp/result', { opponentId, won });
  }

  async getPvpTierRewards() {
    return this.request('GET', '/api/pvp/tier-rewards');
  }

  async claimPvpTierReward(tier) {
    return this.request('POST', '/api/pvp/tier-rewards/claim', { tier });
  }

  // PvP Analytics
  async getPvpAnalytics(type, cardId) {
    const endpoint = `/api/pvp/analytics/${type}`;
    const query = cardId ? `?card=${encodeURIComponent(cardId)}` : '';
    return this.request('GET', `${endpoint}${query}`);
  }

  // Attendance (출석)
  async getAttendance() {
    return this.request('GET', '/api/attendance');
  }

  async checkIn() {
    return this.request('POST', '/api/attendance/check-in');
  }

  // Quest (퀘스트)
  async getDailyQuests() {
    return this.request('GET', '/api/quest/daily');
  }

  async claimQuestReward(questId) {
    return this.request('POST', `/api/quest/claim/${questId}`);
  }

  // Battle Pass (배틀패스)
  async getBattlePassSeason() {
    return this.request('GET', '/api/battlepass/current');
  }

  async getBattlePassProgress() {
    return this.request('GET', '/api/battlepass/progress');
  }

  async claimBattlePassReward(tier, rewardType) {
    return this.request('POST', `/api/battlepass/claim/${tier}`, { reward_type: rewardType });
  }

  async buyBattlePassPremium() {
    return this.request('POST', '/api/battlepass/premium');
  }

  // Party (코옵)
  async createParty(dungeonId = null) {
    return this.request('POST', '/api/party/create', { dungeonId });
  }

  async joinParty(inviteCode) {
    return this.request('POST', '/api/party/join', { inviteCode });
  }

  async getPartyStatus() {
    return this.request('GET', '/api/party/status');
  }

  async leaveParty() {
    return this.request('POST', '/api/party/leave');
  }

  async disbandParty() {
    return this.request('POST', '/api/party/disband');
  }

  async toggleReady() {
    return this.request('POST', '/api/party/ready');
  }

  async startPartyDungeon(dungeonId) {
    return this.request('POST', '/api/party/start', { dungeonId });
  }

  async initPartyBattle(partyId, dungeonId, floor) {
    return this.request('POST', '/api/party/battle/init', { partyId, dungeonId, floor });
  }

  async getPartyBattleState(partyId) {
    return this.request('GET', `/api/party/battle/state?partyId=${partyId}`);
  }

  // Mailbox (우편함)
  async getMailbox() {
    return this.request('GET', '/api/mailbox');
  }

  async readMail(mailId) {
    return this.request('POST', '/api/mailbox/read', { mailId });
  }

  async claimMail(mailId) {
    return this.request('POST', '/api/mailbox/claim', { mailId });
  }

  async claimAllMail() {
    return this.request('POST', '/api/mailbox/claim-all');
  }

  // Guild (길드)
  async getGuildList() {
    return this.request('GET', '/api/guild/list');
  }

  async getMyGuild() {
    return this.request('GET', '/api/guild/my');
  }

  async createGuild(name, description) {
    return this.request('POST', '/api/guild/create', { name, description });
  }

  async joinGuild(guildId) {
    return this.request('POST', `/api/guild/join/${guildId}`);
  }

  async leaveGuild() {
    return this.request('POST', '/api/guild/leave');
  }

  async getGuildMessages() {
    return this.request('GET', '/api/guild/messages');
  }

  async sendGuildMessage(message) {
    return this.request('POST', '/api/guild/message', { message });
  }

  async donateGuild(amount) {
    return this.request('POST', '/api/guild/donate', { amount });
  }

  async getGuildBoss() {
    return this.request('GET', '/api/guild/boss');
  }

  async syncGuildBossDamage(damage) {
    return this.request('POST', '/api/guild/boss/sync', { damage });
  }
}

export const api = new ApiClient();
